import type { ReactNode } from 'react';
import { Kbd } from './Kbd';
import styles from './OptionCard.module.css';

export interface OptionCardProps {
  title: string;
  hint?: string;
  /** The key that picks this card, shown in the corner. */
  shortcut?: string;
  /** Art or logos at the top of the card. */
  visual?: ReactNode;
  selected?: boolean;
  /** Shown but not pickable yet, with this word instead of the key. */
  soonLabel?: string;
  size?: 'md' | 'lg';
  /** Shown as under the pointer without one, for a scripted cursor (the landing's How it works). */
  hovered?: boolean;
  onSelect?: () => void;
}

/** One choice on a card. Pressing its key or clicking it picks it. */
export function OptionCard({ title, hint, shortcut, visual, selected = false, soonLabel, size = 'md', hovered, onSelect }: OptionCardProps) {
  const soon = Boolean(soonLabel);
  // A card with nothing at the top does not keep the room for it.
  const sizeClass = size === 'md' && !visual ? styles.compact : styles[size];
  const classes = [styles.card, sizeClass, selected ? styles.selected : '', soon ? styles.soon : ''].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={classes}
      aria-pressed={soon ? undefined : selected}
      aria-disabled={soon || undefined}
      // The drawings (`case/art.module.css`) act out on this as they do on :hover.
      data-hovered={hovered || undefined}
      onClick={soon ? undefined : onSelect}
    >
      <span className={styles.top}>
        <span className={styles.visual}>{visual}</span>
        {soon ? <span className={styles.soonTag}>{soonLabel}</span> : shortcut ? <Kbd tone={selected ? 'inverse' : 'default'}>{shortcut}</Kbd> : null}
      </span>
      <span className={styles.text}>
        {/* Named so motion can lift the title off a picked card. */}
        <span className={styles.title} data-option-title>
          {title}
        </span>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </span>
    </button>
  );
}
